import advance1x from '@/assets/images/inAppTrials/advance/1x.png';
import advance2x from '@/assets/images/inAppTrials/advance/2x.png';
import advance3x from '@/assets/images/inAppTrials/advance/3x.png';
import constellationPro1x from '@/assets/images/inAppTrials/constellationPro/1x.png';
import constellationPro2x from '@/assets/images/inAppTrials/constellationPro/2x.png';
import constellationPro3x from '@/assets/images/inAppTrials/constellationPro/3x.png';
import creators1x from '@/assets/images/inAppTrials/creators/1x.png';
import creators2x from '@/assets/images/inAppTrials/creators/2x.png';
import creators3x from '@/assets/images/inAppTrials/creators/3x.png';
import engage1x from '@/assets/images/inAppTrials/engage/1x.png';
import engage2x from '@/assets/images/inAppTrials/engage/2x.png';
import engage3x from '@/assets/images/inAppTrials/engage/3x.png';
import premium1x from '@/assets/images/inAppTrials/premium/1x.png';
import premium2x from '@/assets/images/inAppTrials/premium/2x.png';
import premium3x from '@/assets/images/inAppTrials/premium/3x.png';
import socialListening1x from '@/assets/images/inAppTrials/socialListening/1x.png';
import socialListening2x from '@/assets/images/inAppTrials/socialListening/2x.png';
import socialListening3x from '@/assets/images/inAppTrials/socialListening/3x.png';
import { extractEnumValue } from '@/models/enum.utils';

export const PRODUCT_MAP = Object.freeze({
  GROW: 'GROW',
  ENGAGE: 'ENGAGE',
  ADVANCE: 'ADVANCE',
  ENTERPRISE: 'ENTERPRISE',
  PREMIUM_ANALYTICS: 'PREMIUM_ANALYTICS',
  ADVANCE_PREMIUM: 'ADVANCE_PREMIUM',
  SOCIAL_LISTENING: 'SOCIAL_LISTENING',
  CREATORS: 'CREATORS',
  CONSTELLATION_PRO: 'CONSTELLATION_PRO',
});

export const BRAND_PRODUCT_LABEL_MAP = Object.freeze({
  GROW: 'grow',
  CREATOR_MANAGEMENT: 'creator_management',
  ENGAGE: 'engage',
  ADVANCE: 'advance',
  ENTERPRISE: 'enterprise',
  PREMIUM_ANALYTICS: 'premium_analytics',
  CREATOR_STARTER: 'creator_starter',
  CREATOR_PRO: 'creator_pro',
  CREATOR_PLUS: 'creator_plus',
  CREATOR_CUSTOM: 'creator_custom',
});

export const CUSTOMER_PLAN = Object.freeze({
  GROW: {
    name: 'Grow',
    value: 'grow',
  },
  CREATOR_MANAGEMENT: {
    name: 'Creator Management',
    value: 'creator_management',
  },
  ENGAGE: {
    name: 'Engage',
    value: 'engage',
  },
  ADVANCE: {
    name: 'Advance',
    value: 'advance',
  },
  ADVANCE_SOCIAL_LISTENING: {
    name: 'Advance Social Listening',
    value: 'advance_social_listening',
  },
  ADVANCE_PREMIUM: {
    name: 'Advance Premium',
    value: 'advance_premium',
  },
  ADVANCE_PREMIUM_AND_SOCIAL_LISTENING: {
    name: 'Advance Premium and Social Listening',
    value: 'advance_premium_and_social_listening',
  },
  ENTERPRISE: {
    name: 'Enterprise',
    value: 'enterprise',
  },
  ENTERPRISE_PREMIUM: {
    name: 'Enterprise Premium',
    value: 'enterprise_premium',
  },
  ENTERPRISE_SOCIAL_LISTENING: {
    name: 'Enterprise Social Listening',
    value: 'enterprise_social_listening',
  },
  ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING: {
    name: 'Enterprise Premium and Social Listening',
    value: 'enterprise_premium_and_social_listening',
  },
  // Legacy plans
  AGENCY: {
    name: 'Agency',
    value: 'agency',
  },
  EMERGING: {
    name: 'Emerging',
    value: 'emerging',
  },
  HIGH_GROWTH: {
    name: 'High Growth',
    value: 'high_growth',
  },
  STRATEGIC: {
    name: 'Strategic',
    value: 'strategic',
  },
  FREE: {
    name: 'Free',
    value: 'free',
  },
  BASIC: {
    name: 'Basic',
    value: 'basic',
  },
  TEST: {
    name: 'Test',
    value: 'test',
  },
});

const FEATURE_MAP = Object.freeze({
  ANALYTICS: { routeName: 'analytics', requiredProduct: PRODUCT_MAP.GROW },
  CAMPAIGNS: { routeName: 'campaigns', requiredProduct: PRODUCT_MAP.PREMIUM_ANALYTICS },
  COMMUNITY: { routeName: 'community', requiredProduct: PRODUCT_MAP.ENGAGE },
  COMPETITORS: { routeName: 'competitors', requiredProduct: PRODUCT_MAP.PREMIUM_ANALYTICS },
  DASHBOARDS: { routeName: 'dashboards', requiredProduct: PRODUCT_MAP.ADVANCE },
  BOARDS: { routeName: 'boards', requiredProduct: PRODUCT_MAP.ENGAGE },
  LIBRARY: { routeName: 'library', requiredProduct: PRODUCT_MAP.ENGAGE },
  LIKESHOP: { routeName: 'likeshop', requiredProduct: PRODUCT_MAP.GROW },
  RELATIONSHIPS: { routeName: 'relationships', requiredProduct: PRODUCT_MAP.PREMIUM_ANALYTICS },
  SCHEDULER: { routeName: 'scheduler', requiredProduct: PRODUCT_MAP.GROW },
  SOCIAL_LISTENING: { routeName: 'listening', requiredProduct: PRODUCT_MAP.SOCIAL_LISTENING },
  CREATORS: { routeName: 'creators', requiredProduct: PRODUCT_MAP.CREATORS },
  TIKTOK_DISCOVERY: { routeName: 'tiktokDiscovery', requiredProduct: PRODUCT_MAP.CREATORS },
  CONSTELLATION_PRO: {
    routeName: 'constellation-pro',
    requiredProduct: PRODUCT_MAP.CONSTELLATION_PRO,
  },
});

export const FEATURE_UPGRADE_PLACEHOLDER_CTA_TEXT_MAP = Object.freeze({
  dashboards: 'Try Dashboards',
  community: 'Try Community',
  library: 'Try Library',
  listening: 'Try Social Listening',
  competitors: 'Try Competitive',
  campaigns: 'Try Campaigns',
  creators: 'Try Creators',
  boards: 'Try Boards',
  tiktokDiscovery: 'Try Creators',
  'constellation-pro': 'Try Constellation Pro',
});

export const START_TRIAL = Object.freeze({
  [PRODUCT_MAP.ENGAGE]: {
    product_name: 'Engage',
    product_label: 'engage',
    product_type: 'Social Plan',
    title: 'Start a 14-Day Trial of Engage',
    description_items: [
      'Never miss a moment to connect with your community on social',
      'Find out the best times to post on social based on when your audience is active',
      'Store all of your owned and earned content in our Library',
    ],
    imageSrc: engage3x,
    imageSrcSet: [engage3x, engage2x, engage1x],
    trackingKey: 'plan',
  },

  [PRODUCT_MAP.ADVANCE]: {
    product_name: 'Advance',
    product_label: 'advance',
    product_type: 'Social Plan',
    title: 'Start a 14-Day Trial of Advance',
    description_items: [
      'Streamline reporting across your channels in Dashboards',
      'Make better content decisions using Vision AI',
      "Get the inside scoop of what's happening within your industry in Competitors",
      'Discover and engage with UGC featuring your brand',
    ],
    imageSrc: advance3x,
    imageSrcSet: [advance3x, advance2x, advance1x],
    trackingKey: 'plan',
  },

  [PRODUCT_MAP.PREMIUM_ANALYTICS]: {
    product_name: 'Premium Analytics',
    product_label: 'premium_analytics',
    product_type: 'Add-On',
    title: 'Start a 14-Day Trial of Premium Analytics',
    description_items: [
      'Measure your initiatives across social channels in Campaigns',
      'Ask Vision AI to analyze your owned, competitor and UGC data for reports in seconds',
    ],
    imageSrc: premium3x,
    imageSrcSet: [premium3x, premium2x, premium1x],
    trackingKey: 'addOn',
  },

  [PRODUCT_MAP.ADVANCE_PREMIUM]: {
    product_name: 'Advance + Premium Analytics',
    product_label: 'advance_premium',
    product_type: 'Add-On',
    title: 'Start a 14-Day Free Trial of Advance with the Premium Analytics Add-On',
    description_items: [
      'Streamline reporting across your channels in Dashboards',
      'Make better content decisions using Vision AI',
      "Get the inside scoop of what's happening within your industry in Competitors",
      'Discover and engage with UGC featuring your brand',
      'Measure your initiatives across social channels in Campaigns',
      'Ask Vision AI to analyze your owned, competitor and UGC data for reports in seconds',
    ],
    imageSrc: advance3x,
    imageSrcSet: [advance3x, advance2x, advance1x],
    trackingKey: 'plan',
  },

  [PRODUCT_MAP.SOCIAL_LISTENING]: {
    product_name: 'Social Listening',
    product_label: 'social_listening_tier_trial',
    product_type: 'Add-On',
    title: 'Start a 14-Day Trial of Social Listening',
    description_items: [
      'Keep a pulse on the conversations around your brand, products, competitors or campaigns',
      'Instantly see what’s trending across Instagram, X and YouTube',
      'View sentiment and brand health reports alongside your most important metrics in Dashboards',
    ],
    imageSrc: socialListening3x,
    imageSrcSet: [socialListening3x, socialListening2x, socialListening1x],
    trackingKey: 'addOn',
  },
  [PRODUCT_MAP.CREATORS]: {
    chilipiper: {
      router: 'creators-demo',
      form: 'creators_demo',
    },
    product_name: 'Creator Management Plan',
    product_label: 'social_listening_tier_trial',
    product_type: 'Creator Management Plan',
    title: 'Start a 14-day Trial of Creator Management',
    description_items: [
      'Find creators who resonate with your brand audience',
      'Outreach and track campaign deliverables-including Stories',
      'Measure creator performance and ROI',
      "Capture all social efforts with Dash Social's social Management platform",
    ],
    imageSrc: creators3x,
    imageSrcSet: [creators3x, creators2x, creators1x],
    trackingKey: 'addOn',
  },
  [PRODUCT_MAP.CONSTELLATION_PRO]: {
    product_name: 'Constellation Pro',
    product_label: 'constellation_pro',
    product_type: 'Constellation Pro Plan',
    title: 'Start a 14-day Trial of Constellation Pro',
    description_items: [
      'Configure brand guidelines, complementing what Vision already knows',
      'Generate on-brand images with or without prompting',
      'Animate images into motion videos',
      'Produce assets at scale, in various languages and aspect ratios',
    ],
    imageSrc: constellationPro3x,
    imageSrcSet: [constellationPro3x, constellationPro2x, constellationPro1x],
    trackingKey: 'addOn',
  },
});

export function isSameCustomerPlan(left, right) {
  const leftType = extractEnumValue(left);
  const rightType = extractEnumValue(right);

  // make sure we have a valid plan type
  if (!leftType || !rightType) {
    return false;
  }

  return leftType === rightType;
}

export function isProPlan(type) {
  return [BRAND_PRODUCT_LABEL_MAP.CREATOR_PRO].includes(extractEnumValue(type));
}

export function isStarterPlan(type) {
  return [BRAND_PRODUCT_LABEL_MAP.CREATOR_STARTER].includes(extractEnumValue(type));
}

export function isPlusPlan(type) {
  return [BRAND_PRODUCT_LABEL_MAP.CREATOR_PLUS].includes(extractEnumValue(type));
}

export function isCustomPlan(type) {
  return [BRAND_PRODUCT_LABEL_MAP.CREATOR_CUSTOM].includes(extractEnumValue(type));
}

export function isAdvancePlan(type) {
  return [
    CUSTOMER_PLAN.ADVANCE.value,
    CUSTOMER_PLAN.ADVANCE_PREMIUM.value,
    CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING.value,
    CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING.value,
  ].includes(extractEnumValue(type));
}

export function isEnterprisePlan(type) {
  return [
    CUSTOMER_PLAN.ENTERPRISE.value,
    CUSTOMER_PLAN.ENTERPRISE_PREMIUM.value,
    CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING.value,
    CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING.value,
  ].includes(extractEnumValue(type));
}

export function isPremiumPlan(type) {
  return [
    CUSTOMER_PLAN.ADVANCE_PREMIUM.value,
    CUSTOMER_PLAN.ENTERPRISE_PREMIUM.value,
    CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING.value,
    CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING.value,
  ].includes(extractEnumValue(type));
}

export function isCustomerPlanLegacy(type) {
  return [
    CUSTOMER_PLAN.AGENCY.value,
    CUSTOMER_PLAN.EMERGING.value,
    CUSTOMER_PLAN.HIGH_GROWTH.value,
    CUSTOMER_PLAN.STRATEGIC.value,
    CUSTOMER_PLAN.FREE.value,
    CUSTOMER_PLAN.BASIC.value,
    CUSTOMER_PLAN.TEST.value,
  ].includes(extractEnumValue(type));
}

export function removeAddOnFromPlan(type) {
  return extractEnumValue(type).split('_')[0];
}

export function getRequiredProductForFeature(feature) {
  const mappedFeature = Object.values(FEATURE_MAP).find((item) => item.routeName === feature);
  return mappedFeature.requiredProduct;
}
